/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { ICartAmountContext, IChatContext, TContextProvider } from '../../../interfaces';
import { ProactiveChatNames } from '../../helpers';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * CartAmountTrigger class.
 * Proactive chat is triggered if cart amount is in preset boundaries (config.proactiveOnCartAmountMin, config.proactiveOnCartAmountMax).
 */
export default class CartAmountTrigger extends ProactiveChatTrigger {
    private min?: number;
    private max?: number;
    private totalInCart: number = 0;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.min = this.props.config.proactiveOnCartAmountMin;
        this.max = this.props.config.proactiveOnCartAmountMax;

        this.props.context.telemetry.debug(
            `[Chat][${CartAmountTrigger.name}] Cart amount proactive chat initialized with min: '${this.min}'; max: '${this.max}'.`
        );
    }

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: the total amount in the cart should be within the min and max values.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        const cart = await this._getCart();
        if (cart.isEmpty) {
            this.props.context.telemetry.debug(`[Chat][${CartAmountTrigger.name}] Cart was empty. Validation returning 'false'.`);
            return false;
        }

        this.totalInCart = cart.cart.TotalAmount || 0;

        let retVal: boolean = false;
        if (this.min) {
            if (this.max) {
                retVal = this.min <= this.totalInCart && this.totalInCart <= this.max;
            } else {
                retVal = this.min <= this.totalInCart;
            }
        } else if (this.max) {
            retVal = this.totalInCart <= this.max;
        }

        this.props.context.telemetry.debug(
            `[Chat][${CartAmountTrigger.name}] Validation result: '${retVal}'; Cart total: '${this.totalInCart}'.`
        );
        return retVal;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): ICartAmountContext => {
            return {
                ...this.context,
                eCommerceCustIsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                eCommerceCustProactiveType: {
                    value: ProactiveChatNames.CartAmount,
                    isDisplayable: true
                },
                eCommerceCustProactiveShoppingCartAmount: {
                    value: this.totalInCart,
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnCartAmountMessage || this.props.config.proactiveDefaultMessage;
    }

    /**
     * Gets the cart.
     * @returns {Promise<ICartState} The cart.
     */
    private async _getCart(): Promise<ICartState> {
        return getCartState(this.props.context.actionContext);
    }
}
